import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import { withFormik, Form, Field } from 'formik';
import Card, { CardSection } from '@lightspeed/cirrus-card';
import { Divider } from '@lightspeed/flame/Divider';
import Input from '@lightspeed/cirrus-input';
import Text, { TextLink } from '@lightspeed/cirrus-text';
import { IconVerified } from '@lightspeed/flame/Icon/Verified';
import { IconWarning } from '@lightspeed/flame/Icon/Warning';
import { IconDanger } from '@lightspeed/flame/Icon/Danger';
import { IconInfo } from '@lightspeed/flame/Icon/Info';

import CountryDropdown from 'cirrus/Dropdown/Country';
import {
  getCountryCodeFromCountryName,
  getL10nForCountryCode,
} from 'cirrus/Dropdown/Country/utils';
import RegionDropdown from 'cirrus/Dropdown/Region';
import { hasRegions, getRegionFromRegionName } from 'cirrus/Dropdown/Region/utils';
import FormField from 'cirrus/FormField';
import StaticAddress from 'pages/components/StaticAddress';
import Header from './Header';
import Select from './Select';
import EditableField from './EditableField';

import {
  getShopAddress,
  getShopListingByShopId,
  hasValidAddress,
  createInitialErrors,
  createStatusOnError,
} from './utils';
import schema from './validations';

const statusIcon = {
  valid: <IconVerified color="green" />,
  warning: <IconWarning color="orange" />,
  error: <IconDanger color="maple-200" />,
};

// Firefox does not support `word-break`, so we have to put `word-wrap` as well.
// See https://stackoverflow.com/a/46113930
const breakWordStyle = {
  wordBreak: 'break-word',
  wordWrap: 'break-word',
};

const FormikInput = ({ field, form, ...rest }) => (
  <Input {...field} data-test={`input-${field.name}`} {...rest} />
);

const FormRow = ({ status, label, description, controlId, showWarning, content, ...rest }) => (
  <div
    className="cr-flex cr-justify-between"
    data-test={controlId ? `formrow-${controlId}` : null}
    {...rest}
  >
    <div className="cr-width-7-12 cr-inline-flex cr-pr-2">
      <div style={{ marginTop: '3px' }}>{statusIcon[status]}</div>
      <div className="cr-pl-3">
        <label htmlFor={controlId}>
          <Text body bold>
            {label}
            {showWarning && '*'}
          </Text>
        </label>
        <Text bodySmall>{description}</Text>
      </div>
    </div>
    <div className="cr-width-5-12">
      {controlId ? <Field name={controlId} render={content} /> : content()}
    </div>
  </div>
);

const formikSettings = {
  validationSchema: schema,
  isInitialValid: props => !Object.keys(props.initialErrors).length,
  mapPropsToValues(props) {
    return props.initialValues;
  },
  handleSubmit(values, actions) {
    actions.props.onSubmit(values, actions);
  },
};

const taxModelOptions = [
  { value: 1, label: 'Tax Inclusive' },
  { value: 2, label: 'Tax Exclusive' },
];

class EcomProvisioningForm extends Component {
  constructor(props) {
    super(props);

    // Because Formik cannot handle initial errors yet, we have to create the
    // error object ourself on first render and then swap them out on the second
    // render.
    // See: https://github.com/jaredpalmer/formik/issues/288
    this.initialErrors = this.props.initialErrors;
    this.props.validateForm(); // Triggers async re-render
    this.useInitialErrors = true;

    this.getErrors = this.getErrors.bind(this);
    this.handleSelectChange = this.handleSelectChange.bind(this);
    this.handleShopIdSelect = this.handleShopIdSelect.bind(this);
    this.handleCountrySelectChange = this.handleCountrySelectChange.bind(this);
    this.handleCountryInputChange = this.handleCountryInputChange.bind(this);
  }

  /**
   * NOTE: This method is only needed, because formik doesn't allow for
   * setting the initial errors on the first render.
   */
  getErrors() {
    const errors = this.useInitialErrors ? this.initialErrors : this.props.errors;

    this.useInitialErrors = false;

    return errors;
  }

  handleCountrySelectChange(option) {
    const { setFieldValue } = this.props;

    let { regionName } = this.props.values;
    let regionCode = '';
    const { countryCode = '', countryName = '' } = option || {};

    if (countryCode && hasRegions(countryCode)) {
      ({ regionCode, regionName } = getRegionFromRegionName({
        countryCode,
        regionName: regionCode || regionName,
      }));
    }

    setFieldValue('countryCode', countryCode);
    setFieldValue('countryName', countryName);
    setFieldValue('regionCode', regionCode);
    setFieldValue('regionName', regionName);
  }

  handleCountryInputChange(inputValue) {
    const { setFieldValue } = this.props;

    setFieldValue('countryName', inputValue);

    return inputValue;
  }

  handleSelectChange(name) {
    const { setFieldValue } = this.props;

    return option => {
      setFieldValue(name, option ? option.value : '');
    };
  }

  /* eslint-disable class-methods-use-this */
  goToSettingsGeneralOptions(e) {
    window.navigateToContent('view', 'admin.views.options', '1');
    e.preventDefault();
  }
  /* eslint-enable class-methods-use-this */

  handleShopIdSelect(option) {
    const { value: shopId } = option || {};
    const { values, setValues, setErrors, staticData } = this.props;
    const { shopListings } = staticData;
    const shop = getShopListingByShopId(shopListings, shopId);

    if (!shop) return;

    const countryName = get(shop, 'countryName') || '';

    const valuesToApply = {
      shopId,
      countryName,
      shopEmail: get(shop, 'email') || '',
      countryCode: get(shop, 'countryCode') || getCountryCodeFromCountryName(countryName) || '',
      regionCode: get(shop, 'regionCode') || '',
      regionName: get(shop, 'regionName') || '',
      address1: get(shop, 'address1') || '',
      address2: get(shop, 'address2') || '',
      city: get(shop, 'city') || '',
      zipcode: get(shop, 'zipcode') || '',
    };

    const newValues = {
      ...values,
      ...valuesToApply,
    };

    // Because Formik only validates after the values are set
    // the "edit"-mode cannot properly re-evaluate whether it should be
    // on or off.
    setErrors(createInitialErrors(newValues));
    setValues(newValues);
  }

  render() {
    const {
      staticData,
      ecomLanguagesOptions,
      shopListingsOptions,
      publishOptions,
      values,
      isValid,
      isSubmitting,
      handleSubmit,
    } = this.props;
    const { ecomProvisioning, shopListings } = staticData;
    const { taxPricing } = ecomProvisioning;

    const errors = this.getErrors();
    const showRegionDropdown = hasRegions(values.countryCode);
    const hasMoreThanOneShop = shopListings.length > 1;
    const isAddressValid = hasValidAddress(errors);
    const regionStatus = createStatusOnError(errors.regionName || errors.regionCode);
    const l10nTranslations = getL10nForCountryCode(values.countryCode).translations;

    return (
      <Form>
        <Header isDisabled={!isValid} isSubmitting={isSubmitting} onInstallClick={handleSubmit} />
        <Card large>
          <CardSection noSpacing>
            <div
              className="cr-pv-1 cr-ph-4 cr-bg-snow-200 cr-text-right"
              style={{
                borderTopLeftRadius: 'calc(0.375rem - 1px)',
                borderTopRightRadius: 'calc(0.375rem - 1px)',
              }}
            >
              <IconInfo color="blue" />
              <Text bodySmall color="gray" className="cr-pl-1" style={{ display: 'inline-block' }}>
                Changes to fields marked with a * will be saved for both Retail and eCom.
              </Text>
            </div>
          </CardSection>

          <Divider />

          <CardSection>
            <FormRow
              status={errors.shopName ? 'warning' : 'valid'}
              label="eCom Store Name"
              description="How your shop will appear in Google Searches."
              controlId="shopName"
              content={({ field }) => (
                <FormField status={createStatusOnError(errors.shopName)}>
                  <Input {...field} type="text" className="cr-width-full" />
                </FormField>
              )}
            />
          </CardSection>

          <Divider />

          <Fragment>
            <CardSection>
              {hasMoreThanOneShop && (
                <CardSection>
                  <FormRow
                    status={errors.shopId ? 'warning' : 'valid'}
                    label="Synchronize from Location"
                    description="Quantity on hand shown online will be synchronized with this retail location."
                    controlId="shopId"
                    content={({ field }) => (
                      <FormField status={createStatusOnError(errors.shopId)}>
                        <Select
                          placeholder="Select a Location"
                          initialValue={field.value}
                          inputProps={{ id: field.name, name: field.name }}
                          options={shopListingsOptions}
                          onSelectChange={this.handleShopIdSelect}
                          noTranslate
                        />
                      </FormField>
                    )}
                  />
                </CardSection>
              )}
              <CardSection>
                <FormRow
                  status={isAddressValid ? 'valid' : 'warning'}
                  label="Shop Address"
                  description="To be displayed on your eCommerce store."
                  data-test="formrow-shopaddress"
                  showWarning
                  content={() => (
                    <EditableField
                      watchValue={values.shopId}
                      initialIsEditing={!isAddressValid}
                      whenEditing={() => (
                        <div data-test="address-form">
                          <div>
                            <FormField
                              id="countryCode"
                              label="Country"
                              status={createStatusOnError(errors.countryCode)}
                            >
                              <CountryDropdown
                                countryCode={values.countryCode}
                                countryName={values.countryName}
                                onSelectChange={this.handleCountrySelectChange}
                                onInputChange={this.handleCountryInputChange}
                                placeholder="Select a Country"
                                noTranslate
                              />
                            </FormField>
                          </div>
                          <div className="cr-pt-2">
                            <FormField status={createStatusOnError(errors.address1)}>
                              <Field
                                component={FormikInput}
                                type="text"
                                name="address1"
                                label="Address"
                                className="cr-width-full"
                                placeholder="Enter an Address"
                              />
                            </FormField>
                          </div>
                          <div className="cr-pt-2">
                            <FormField status={createStatusOnError(errors.address2)}>
                              <Field
                                component={FormikInput}
                                type="text"
                                name="address2"
                                placeholder="Suite, unit, floor, building, etc. (optional)"
                                className="cr-width-full"
                              />
                            </FormField>
                          </div>
                          <div className="cr-flex cr-pt-2">
                            <div className="cr-flex-1 cr-pr-2">
                              <FormField status={createStatusOnError(errors.city)}>
                                <Field
                                  component={FormikInput}
                                  type="text"
                                  name="city"
                                  label="City"
                                  placeholder="Enter a City"
                                />
                              </FormField>
                            </div>

                            <div className="cr-flex-1 cr-pr-2">
                              {showRegionDropdown ? (
                                <Field
                                  name="regionCode"
                                  render={() => (
                                    <FormField
                                      id="region"
                                      label={l10nTranslations.state}
                                      status={regionStatus}
                                    >
                                      <RegionDropdown
                                        placeholder={`Select a ${l10nTranslations.state}`}
                                        countryCode={values.countryCode}
                                        regionName={values.regionName}
                                        regionCode={values.regionCode}
                                        onChange={region => {
                                          const { regionCode = '', regionName = '' } = region || {};
                                          this.props.setFieldValue('regionName', regionName);
                                          this.props.setFieldValue('regionCode', regionCode);
                                        }}
                                        noTranslate
                                      />
                                    </FormField>
                                  )}
                                />
                              ) : (
                                <FormField status={createStatusOnError(errors.regionName)}>
                                  <Field
                                    component={FormikInput}
                                    name="regionName"
                                    type="text"
                                    label={l10nTranslations.state}
                                    className="cr-width-full"
                                    placeholder={`Enter a ${l10nTranslations.state}`}
                                  />
                                </FormField>
                              )}
                            </div>

                            <div className="cr-flex-1">
                              <FormField status={createStatusOnError(errors.zipcode)}>
                                <Field
                                  component={FormikInput}
                                  type="text"
                                  name="zipcode"
                                  label={l10nTranslations.zip}
                                  placeholder={`Enter a ${l10nTranslations.zip}`}
                                  className="cr-width-full"
                                />
                              </FormField>
                            </div>
                          </div>
                        </div>
                      )}
                      whenNotEditing={() => (
                        <Text element="div" body data-test="address-static">
                          <StaticAddress
                            format={values.countryCode}
                            address={getShopAddress(values)}
                          />
                        </Text>
                      )}
                    />
                  )}
                />
              </CardSection>
              <CardSection>
                <FormRow
                  status={errors.shopEmail ? 'warning' : 'valid'}
                  label="Shop Email"
                  description="For email sent on your behalf for e-receipts, order confirmations, etc."
                  controlId="shopEmail"
                  showWarning
                  content={({ field }) => (
                    <EditableField
                      watchValue={values.shopId}
                      initialIsEditing={!!errors.shopEmail}
                      whenEditing={() => (
                        <FormField status={createStatusOnError(errors.shopEmail)}>
                          <Input
                            {...field}
                            type="text"
                            className="cr-width-full"
                            placeholder="Enter an Email"
                            data-type="email"
                          />
                        </FormField>
                      )}
                      whenNotEditing={() => (
                        <Text body style={breakWordStyle}>
                          {field.value}
                        </Text>
                      )}
                    />
                  )}
                />
              </CardSection>
            </CardSection>
          </Fragment>

          <Divider />

          <Fragment>
            <CardSection>
              <CardSection>
                <FormRow
                  status={errors.languageCode ? 'warning' : 'valid'}
                  label="Default eCom Language"
                  description={
                    <Fragment>
                      The language currently used for item descriptions in your Retail account.
                      <br />
                      (Once eCom is installed, this can’t be changed.)
                    </Fragment>
                  }
                  controlId="languageCode"
                  content={({ field }) => (
                    <FormField status={createStatusOnError(errors.languageCode)}>
                      <Select
                        placeholder="Select a language"
                        inputProps={{ id: field.name, name: field.name }}
                        options={ecomLanguagesOptions}
                        onSelectChange={this.handleSelectChange(field.name)}
                        noLocalize
                      />
                    </FormField>
                  )}
                />
              </CardSection>
              <CardSection>
                <FormRow
                  status="valid"
                  label="Tax Model"
                  description={
                    <Fragment>
                      To change this, go to{' '}
                      <TextLink
                        href="/?name=admin.views.options&form_name=view&id=1&tab=settings"
                        bold
                        onClick={this.goToSettingsGeneralOptions}
                      >
                        Settings &gt; General Options
                      </TextLink>
                      .<br />
                      (Must match the tax model used at your physical shop.)
                    </Fragment>
                  }
                  controlId="tax"
                  content={({ field }) => (
                    <Select
                      initialValue={taxPricing === 'tax_inclusive' ? 1 : 2}
                      tabIndex="-1"
                      inputProps={{ id: field.name, name: field.name }}
                      options={taxModelOptions}
                      disabled
                    />
                  )}
                />
              </CardSection>
              <CardSection>
                <FormRow
                  status={errors.publish ? 'warning' : 'valid'}
                  label="eCom Products"
                  description={
                    <Fragment>
                      Decide whether you prefer to publish all your Retail items to eCom now or
                      later.
                    </Fragment>
                  }
                  controlId="publish"
                  content={({ field }) => (
                    <FormField status={createStatusOnError(errors.publish)}>
                      <Select
                        initialValue={values.publish}
                        inputProps={{ id: field.name, name: field.name }}
                        options={publishOptions}
                        onSelectChange={this.handleSelectChange(field.name)}
                        searchable={false}
                        noTranslate
                      />
                    </FormField>
                  )}
                />
              </CardSection>
            </CardSection>
          </Fragment>

          <Divider />

          <CardSection>
            <FormRow
              status={errors.signInEmail ? 'warning' : 'valid'}
              label="Sign-In Email"
              description="The email used to sign in to your eCom shop"
              controlId="signInEmail"
              showWarning
              content={({ field }) => (
                <EditableField
                  initialIsEditing={!!errors.signInEmail}
                  whenEditing={() => (
                    <FormField status={createStatusOnError(errors.signInEmail)}>
                      <Input
                        {...field}
                        type="text"
                        className="cr-width-full"
                        placeholder="Enter a Sign-In Email"
                        data-type="email"
                      />
                    </FormField>
                  )}
                  whenNotEditing={() => (
                    <Text body style={breakWordStyle}>
                      {field.value}
                    </Text>
                  )}
                />
              )}
            />
          </CardSection>
        </Card>
      </Form>
    );
  }
}

EcomProvisioningForm.propTypes = {
  staticData: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  ecomLanguagesOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  ).isRequired,
  shopListingsOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.number })
  ).isRequired,
  publishOptions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string })
  ).isRequired,
  values: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
  isValid: PropTypes.bool.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  handleSubmit: PropTypes.func.isRequired,
};

const EnhancedComponent = withFormik(formikSettings)(EcomProvisioningForm);

EnhancedComponent.displayName = 'Formik(EcomProvisioningForm)';

export default EnhancedComponent;
